export * from './TypeUtils';
import {ClosedAccount} from '../../client/reducers/payout';
import {
    Affiliate,
    Status,
    StatusReason,
    SocialChannel,
    ChannelStatus,
    Channel,
    SocialPlatform,
    AcceptanceStatus,
    PayoutTypes,
    SlugAvailability,
    PayoutSetupInfo,
    EarningsInfo
} from '../types';

export const isValidDateRange = (d1: Date, d2: Date) => {
    return !d1 || !d2 || d1.getTime() >= d2.getTime();
};

export const isDateExpired = (date, expireInMillis?) => {
    if (!date) return true;

    try {
        let fromTimeInMillis = Date.now();

        if (typeof expireInMillis === 'number') {
            fromTimeInMillis += expireInMillis;
        }

        if (date instanceof Date) {
            return fromTimeInMillis > date.getTime();
        }

        return fromTimeInMillis > new Date(date).getTime();
    } catch (err) {
        console.error(`Invalid format given for date: [${date}].`);
        return true;
    }
};

export const isUniquePlatform = (channels = [], platform) => {
    return channels.findIndex(channel => channel.platform === platform) === -1;
};

export const showJWTLink = (payoutSetupInfo): boolean => {
    if (!payoutSetupInfo) return false;
    const {completed, taxProfileStatus, walletStatus} = payoutSetupInfo;
    return !completed && (taxProfileStatus !== 'NONE' || walletStatus !== 'NONE');
};

export const isValidChannel = <T extends SocialChannel | Channel>(channel: T): boolean => {
    if (!channel) return false;
    const {valid: oAuthed} = channel.oauthDetails || {};
    return channel.channelStatus === ChannelStatus.VALID && oAuthed;
};

/**
 * At least EVERY oAuthed channel provided must meet minimum requirements
 * @param {SocialChannel[]} socialChannels
 * @param {boolean} atleastOne - Minimum one channel should be valid
 */
export function isValidSocialChannels<T extends (SocialChannel | Channel)[]>(
    socialChannels: T,
    atleastOne?: boolean
): boolean {
    if (!socialChannels || !socialChannels.length) return false;
    const cb = ch => isValidChannel(ch);
    return atleastOne ? socialChannels.some(cb) : socialChannels.every(cb);
}

export const isV1 = (affiliate: Affiliate): boolean => {
    const {agreementInfo} = affiliate || {};
    return agreementInfo && agreementInfo.currentVersion.configId === 1;
};

/** returning rejected applicant */
export const onCooldown = ({earliestReapplicationDate, status}: Affiliate) => {
    return status === Status.REJECTED && !isDateExpired(earliestReapplicationDate);
};

/** epic account with no affiliate status */
export const isNewApplicant = <T extends Affiliate>(affiliate: T | Partial<T>) => {
    const {applicationState} = affiliate || {};
    return !applicationState || applicationState === 'NONE';
};

export const isCreativeBuilder = (affiliate: Affiliate) => {
    const {whitelist} = affiliate || {};
    return whitelist && whitelist.isCreative;
};

export const hasAcceptedLatestAgreement = (affiliate: Affiliate): boolean => {
    const {agreementInfo} = affiliate || {};
    return agreementInfo && agreementInfo.currentVersion.obsolete === false;
};

/** existing affiliate rejected new creator agreement */
export const hasRejectedNewTerms = (affiliate: Affiliate): boolean => {
    const {status, statusReason} = affiliate || {};
    return status === Status.DISABLED && statusReason === StatusReason.DISABLED_DECLINED_EULA;
};

export const hasRejectedNewTermsWithRewardsPayout = (
    affiliate: Affiliate,
    payoutType: PayoutTypes
): boolean => {
    if (!affiliate || !payoutType) return false;
    const {status, statusReason} = affiliate;
    return (
        status === Status.DISABLED &&
        statusReason === StatusReason.DISABLED_DECLINED_EULA &&
        // (payoutSetupInfo && payoutSetupInfo.completed) &&
        payoutType === PayoutTypes.CASH
    );
};

export const hasRejectedNewTermsWithVBucks = (closedAccount: ClosedAccount): boolean => {
    if (!closedAccount || typeof closedAccount !== 'object') return false;
    const {acceptanceStatus, payoutType} = closedAccount;
    return acceptanceStatus === AcceptanceStatus.REJECTED && payoutType === PayoutTypes.VBUCKS;
};

export const canReactivate = (affiliate: Affiliate): boolean => {
    return affiliate && affiliate.canReinstate;
};

/** Migrating to new agreement terms and experience */
export const canMigrate = (affiliate: Affiliate): boolean => {
    const {status, agreementInfo} = affiliate || {};
    return status === Status.ACTIVE && agreementInfo && agreementInfo.currentVersion.obsolete;
};

/** Presignup requirements */
export const canSignUp = accountInfo => {
    const {tfaEnabled, emailVerified} = accountInfo || {};
    return tfaEnabled === true && emailVerified === true;
};

export const isFullyDisabled = (affiliate: Affiliate): boolean => {
    const {status} = affiliate || {};
    return !status || status === Status.DISABLED || status === Status.DELETED;
};

/** Eligible Reasons to view portal / warning bar / etc */
export const canEditSettings = (
    affiliate: Affiliate,
    options?: {payoutType?: PayoutTypes}
): boolean => {
    if (!affiliate || affiliate.status !== Status.DISABLED) return false;
    const {statusReason} = affiliate;
    const {payoutType} = options || {};
    return (
        hasRejectedNewTermsWithRewardsPayout(affiliate, payoutType) ||
        [StatusReason.DISABLED_VIOLATED_OAUTH_REQUIREMENTS].includes(statusReason)
    );
};

export const isActiveAffiliate = (affiliate: Affiliate): boolean => {
    const {status} = affiliate || {};
    return status === Status.ACTIVE;
};

/** account can reapply */
export const canReApply = (affiliate: Affiliate): boolean => {
    const {status} = affiliate || {};
    if (status === Status.REJECTED) {
        return !onCooldown(affiliate);
    }
    return isNewApplicant(affiliate);
};

export const isStrikedChannel = (affiliate: Affiliate, channel: SocialChannel): boolean => {
    if (!affiliate || !channel || !channel.id) return false;
    const foundChannel = affiliate.socialChannels.find(sch => sch.id === channel.id);
    return foundChannel && foundChannel.violations && foundChannel.violations.length > 0;
};

/** **IMPORTANT** preserve the order */
export const canRemoveChannel = (
    affiliate: Affiliate,
    channel: SocialChannel,
    skipMainCheck = true
) => {
    if (!channel) return false;
    const {id: isAdded, channelStatus, oauthDetails, platform} = channel || {};
    const {valid: isAuthenticated} = oauthDetails || {};

    if (channelStatus === ChannelStatus.VALIDATING) {
        return false;
    }

    if (
        !(platform in SocialPlatform) ||
        isNewApplicant(affiliate) ||
        canReApply(affiliate) ||
        (canMigrate(affiliate) && isV1(affiliate)) ||
        canReactivate(affiliate)
    ) {
        return true;
    }

    if (!skipMainCheck && channel.mainChannel) {
        return false;
    }

    return isStrikedChannel(affiliate, channel) || (!isAuthenticated && !!isAdded === false);
};

/** Hotfix to auto-select main channel on behalf of user */
export const canAutoSelectMainChannel = (channels: SocialChannel[]): boolean => {
    if (!channels) return false;
    if (!channels.length) return true;
    return !channels.find(ch => ch.mainChannel && isValidChannel(ch));
};

export const isSpecialAlphaNumeric = <T>(value: T): boolean => {
    return /^[a-zA-Z0-9_\.\-]+$/.test(String(value));
};

export const isSpecialDigits = <T>(value: T): boolean => {
    return /^[\d\_\.\-\+]+$/.test(String(value));
};

export const isDigits = <T>(value: T): boolean => {
    return typeof value === 'number' || isSpecialDigits(value);
};

export const isSameCharOnly = <T>(value: T): boolean => {
    return /^(.)\1+$/i.test(String(value));
};

export const hasConsecutiveSpecialChar = <T>(value: T): boolean => {
    return /([\-\._]{2,})+/.test(String(value));
};

export const isStartEndWithSpecialChar = <T>(value: T): boolean => {
    return /^[_\.\-]|[\._\-]$/.test(String(value));
};

export const isValidCreatorCode = (code: string): boolean => {
    if (!code || typeof code !== 'string') return false;

    if (
        code.length < 3 ||
        code.length > 16 ||
        isDigits(code) ||
        isSameCharOnly(code) ||
        !isSpecialAlphaNumeric(code) ||
        hasConsecutiveSpecialChar(code) ||
        isStartEndWithSpecialChar(code)
    ) {
        return false;
    }

    return true;
};

export const canChangeCreatorCode = (
    newCode: string,
    codeAvailability: SlugAvailability,
    affiliate?: {slug: string}
): boolean => {
    const {slug: oldCode = ''} = affiliate || {};
    const {slug: checkedCode = '', available} = codeAvailability || {};
    const _newCode = (newCode || '').toLowerCase();

    if (
        !available || // unavailable
        !checkedCode || // not checked
        !_newCode || // invalid input
        _newCode === oldCode.toLowerCase() || // not unique
        _newCode !== checkedCode.toLowerCase() // not consistent with input
    ) {
        return false;
    }

    return true;
};

export const isPayoutThresholdEligible = (amount: number, compareTo: number = 100) => {
    return Number(amount) >= compareTo;
};

export const isTaxInterviewComplete = (payoutSetupInfo: PayoutSetupInfo) => {
    const {taxProfileStatus} = payoutSetupInfo || {};
    return taxProfileStatus === 'COMPLETED';
};

export const isPayoutMethodComplete = (payoutSetupInfo: PayoutSetupInfo) => {
    const {walletStatus} = payoutSetupInfo || {};
    return walletStatus === 'COMPLETED';
};

export const isPaymentConsentComplete = (payoutSetupInfo: PayoutSetupInfo) => {
    const {paymentConsent} = payoutSetupInfo || {};
    return !!paymentConsent;
};

export const isPayoutEligible = (affiliate: Partial<Affiliate>, amount: number) => {
    const {payoutSetupInfo} = affiliate || {};
    return (
        isPayoutThresholdEligible(amount) &&
        isTaxInterviewComplete(payoutSetupInfo) &&
        isPayoutMethodComplete(payoutSetupInfo) &&
        isPaymentConsentComplete(payoutSetupInfo)
    );
};

export const hasEarnedInCurrentPeriod = (earningsInfo: EarningsInfo): boolean => {
    if (!earningsInfo || !earningsInfo.lastEarningsHitMonth) return false;
    return (
        earningsInfo.earningsWindowStart <= earningsInfo.lastEarningsHitMonth &&
        earningsInfo.lastEarningsHitMonth < earningsInfo.earningsDeadline
    );
};

export const isBlackListed = <T extends string>(value: T, blackList: string[]) => {
    if (!value || !blackList || !blackList.length) return false;
    return !!blackList.find(item => item.toLowerCase() === value.toLowerCase());
};
